import { faBus, faCar } from '@fortawesome/pro-regular-svg-icons';
import { parse } from 'qs';
import BusPriceLoader from './BusPriceLoader';
import { type BusPathData } from './types';
import { type UrlStringParams } from '@search/types';
import { TripSearchForm } from '@search/trip/TripSearchForm';
import {
    provincesToLocations,
    type Province,
    type BusLocation,
    type BusUrlParams,
} from '@utils/bus';
import { ReserveType, VerifyPathError } from '@utils/itinerary';
import { PaxType, type ReserveInfo, SearchMode } from '@utils/types';
import { TripType } from '@utils/trip';
import type { CityListApiApiCityListGetBusCityListGetRequest } from '@clients/BusApi';
import {
    BusCitiesApiGetBusCityInfoGet,
    CityListApiApiCityListGetBusCityListGet,
    CityListApiApiCityListGetTaxiCityListGet,
} from '@clients/BusApi';

export class BusSearchForm extends TripSearchForm<BusLocation, BusPathData> {
    constructor(
        public specificProviderId: number | null,
        public reserveType:
            | ReserveType.Bus
            | ReserveType.Taxi = ReserveType.Bus,
        maxSeatCount = 20
    ) {
        super();
        this.maxSeatCount = maxSeatCount;
    }

    maxSeatCount = 20;

    override popularDomesticOrigins: BusLocation[] = [
        {
            code: 'tehran',
            terminalId: 11320000,
            title: 'تهران',
        },
        {
            code: 'esfahan',
            terminalId: 21310000,
            title: 'اصفهان',
        },
        {
            code: 'mashhad',
            terminalId: 31310000,
            title: 'مشهد',
        },
        {
            code: 'shiraz',
            terminalId: 41310000,
            title: 'شیراز',
        },

        {
            code: 'zahdan',
            terminalId: 61310000,
            title: 'زاهدان',
        },
    ];

    findLocationBaseOnCode(
        code: string,
        locations: BusLocation[]
    ): BusLocation | null {
        return locations.reduce(
            (prevL, l) => {
                if (l.code === code) {
                    return l;
                }
                if (prevL) {
                    return prevL;
                }
                return l.subLocations?.find(s => s.code === code) ?? null;
            },
            null as BusLocation | null
        );
    }

    findLocationBaseOnTerminalId(
        id: number,
        locations: BusLocation[]
    ): BusLocation | null {
        return locations.reduce(
            (prevL, l) => {
                if (l.terminalId === id) {
                    return l;
                }
                if (prevL) {
                    return prevL;
                }
                return l.subLocations?.find(s => s.terminalId === id) ?? null;
            },
            null as BusLocation | null
        );
    }

    override async verifyViewPath(
        path: string,
        pathData: BusPathData
    ): Promise<void> {
        const fullRegex =
            /^\/(?:buses|taxi)\/(\d+)\/(\d+)\/?(\d{4}-\d{2}-\d{2})?\/?(\d{4}-\d{2}-\d{2})?\/?(\d{1,2})?$/;

        if (!fullRegex.test(path)) {
            return super.verifyViewPath(path, pathData);
        }
        const matches = RegExp(fullRegex).exec(path)!;
        await this.searchLocations(matches[1]);
        let origin: BusLocation | null = this.findLocationBaseOnTerminalId(
            Number(matches[1]),
            this.domesticOrigins
        );
        if (!origin) {
            origin = this.findLocationBaseOnTerminalId(
                Number(matches[1]),
                this.foreignOrigins
            );
            this.searchMode = SearchMode.Foreign;
        }

        if (!origin) {
            throw new VerifyPathError('origin not found', 404, false);
        }
        this.setOrigin(origin);
        await this.loadDestinations(matches[2]);
        const destination = this.findLocationBaseOnTerminalId(
            Number(matches[2]),
            this.destinations
        );

        this.searchMode = SearchMode.Domestic;
        if (!destination) {
            throw new VerifyPathError('destination not found', 404, false);
        }
        const newUrl = this.generateViewUrl({
            origin: origin.code,
            destination: destination.code,
            departureDate: matches[3],
            adultCount: matches[5],
            returnDate: matches[4],
        });
        const [url, newQueries] = newUrl.split('?');
        await super.verifyViewPath(url, parse(newQueries) as BusPathData);
        return Promise.reject(
            new VerifyPathError(
                'Path not valid',
                301,
                true,
                url,
                parse(newQueries) as { [key: string]: string }
            )
        );
    }

    override async parsePath(pathData: BusPathData): Promise<BusUrlParams> {
        const { origin, destination } = pathData;
        await this.searchLocations(origin, {
            target: 'origin',
            mode: SearchMode.Domestic,
        });
        let originLocation: BusLocation | null = this.findLocationBaseOnCode(
            origin,
            this.domesticOrigins
        );
        if (!originLocation) {
            originLocation = this.findLocationBaseOnCode(
                origin,
                this.foreignOrigins
            );

            this.searchMode = SearchMode.Foreign;
        }
        if (!originLocation) {
            try {
                const request: { busCityId?: number; code?: string } = {};
                if (/^-?\d+$/.test(origin)) {
                    request.busCityId = +origin;
                } else {
                    request.code = origin;
                }
                const { data } = await BusCitiesApiGetBusCityInfoGet(request);
                originLocation = {
                    code: data.code ?? '',
                    terminalId: data.id ?? 0,
                    title: data.title ?? '',
                    subLocations: data.terminals?.map(terminal => ({
                        code: terminal.code ?? '',
                        terminalId: terminal.id ?? 0,
                        title: terminal.title ?? '',
                    })),
                };
                this.originProvinces.push({
                    provinceName: data.persianTitle ?? '',
                    cities: [
                        {
                            id: data.id!,
                            title: data.title ?? '',
                            isDomestic: data.isDomestic ?? true,
                            isForeign: data.isForeign ?? false,
                            terminals: [],
                            englishTitle: data.englishTitle ?? '',
                            code: data.code,
                        },
                    ],
                });
                this.searchMode = data.isForeign
                    ? SearchMode.Foreign
                    : SearchMode.Domestic;
            } catch (error: any) {
                throw new VerifyPathError(
                    'error',
                    error.response?.status ?? 500
                );
            }
        }

        if (!originLocation) {
            throw new VerifyPathError('origin not found', 404, false);
        }

        this.setOrigin(originLocation);
        await this.loadDestinations(destination);
        let destinationLocation: BusLocation | null =
            this.findLocationBaseOnCode(destination, this.domesticDestinations);

        if (!destinationLocation) {
            destinationLocation = this.findLocationBaseOnCode(
                destination,
                this.foreignDestinations
            );
            this.searchMode = SearchMode.Foreign;
        }
        if (!destinationLocation) {
            throw new VerifyPathError('destination not found', 404, false);
        }
        this.setDestination(destinationLocation);
        return {
            origin,
            destination,
            originTerminalId: originLocation.terminalId,
            destinationTerminalId: destinationLocation.terminalId,
            departureDate:
                (pathData.departureDate && parseDate(pathData.departureDate)) ||
                dayConfig.gregory().add(1, 'day').toDate(),
            adultCount: pathData?.adultCount
                ? parseInt(pathData.adultCount)
                : 1,
            childCount: 0,
            infantCount: 0,
            returnDate:
                (pathData.returnDate && parseDate(pathData.returnDate)) ||
                undefined,
        };
    }

    lastOriginCodeThatDestinationsAreLoadedFor: string | null = null;

    loadDestinationsJob: Promise<void> | null = null;

    async loadDestinations(includeCity?: number | string) {
        if (
            !this.originCode ||
            this.lastOriginCodeThatDestinationsAreLoadedFor === this.originCode
        ) {
            if (this.loadDestinationsJob) {
                await this.loadDestinationsJob;
            }
            return Promise.resolve();
        }
        this.lastOriginCodeThatDestinationsAreLoadedFor = this.originCode;
        const loader = async () => {
            try {
                this.isLoadingLocations = true;
                this.destinationProvinces = [];
                this.setDestination(null);
                const request: CityListApiApiCityListGetBusCityListGetRequest =
                    {
                        fromCity: this.origin?.terminalId,
                        getCities: true,
                        includePopular: this.isDomestic,
                        includeCity: this.destination
                            ? this.destination.terminalId.toString()
                            : includeCity?.toString(),
                        groupTerminals: true,
                    };
                const { data } = await (this.reserveType === ReserveType.Bus
                    ? CityListApiApiCityListGetBusCityListGet(request)
                    : CityListApiApiCityListGetTaxiCityListGet(request));
                if (this.isDomestic) {
                    if (data[0]?.cities?.length) {
                        this.popularDomesticDestinations = provincesToLocations(
                            [{ cities: data[0].cities, provinceName: '' }]
                        );
                    }
                    this.destinationProvinces = data.slice(1);
                } else {
                    this.destinationProvinces = data;
                }
            } catch (error) {
                throw new Error('خطای غیر منتظره ای رخ داد.', { cause: error });
            } finally {
                this.isLoadingLocations = false;
            }
        };
        this.loadDestinationsJob = loader();
        await this.loadDestinationsJob;
    }

    override popularDomesticDestinations: BusLocation[] =
        this.popularDomesticOrigins;

    originProvinces: Province[] = [];

    destinationProvinces: Province[] = [];

    override popularForeignOrigins: BusLocation[] = [
        {
            code: 'tehran',
            terminalId: 11320000,
            title: 'تهران',
        },
        {
            code: 'istanbul_turkey',
            terminalId: 117002,
            title: 'استانبول (ترکیه)',
        },
    ];

    override get domesticDestinations() {
        return provincesToLocations(
            this.destinationProvinces,
            SearchMode.Domestic
        );
    }

    override get domesticOrigins() {
        return provincesToLocations(this.originProvinces, SearchMode.Domestic);
    }

    override get foreignDestinations() {
        return provincesToLocations(
            this.destinationProvinces,
            SearchMode.Foreign
        );
    }

    override get foreignOrigins() {
        return provincesToLocations(this.originProvinces, SearchMode.Foreign);
    }

    override generateViewUrl(params: UrlStringParams<BusUrlParams>) {
        const query = new URLSearchParams();
        if (params.departureDate) {
            query.append('departureDate', params.departureDate);
        }
        if (params.returnDate) {
            query.append('returnDate', params.returnDate);
        }
        if (params.adultCount && params.adultCount !== '1') {
            query.append('adultCount', params.adultCount);
        }
        if (params.childCount && params.childCount !== '0') {
            query.append('childCount', params.childCount);
        }
        if (params.infantCount && params.infantCount !== '0') {
            query.append('infantCount', params.infantCount);
        }
        const q = query.toString();
        return `/${this.reserveType === ReserveType.Bus ? 'buses' : 'taxi'}/${
            params.origin
        }-${params.destination}${q ? `?${q}` : ''}`;
    }

    override getPriceLoader(
        origin: BusLocation,
        destination: BusLocation,
        tripType: TripType
    ): BusPriceLoader {
        return new BusPriceLoader({
            from: `${origin.terminalId}`,
            to: `${destination.terminalId}`,
            specificProviderId: this.specificProviderId,
            tripType,
            adultCount: this.adultCount,
            childCount: this.childCount,
            infantCount: this.infantCount,
        });
    }

    override initPaxCounts = [
        {
            code: PaxType.Adult,
            title: 'مسافر',
            subtitle: '',
            count: 1,
            min: 1,
            max: this.maxSeatCount,
        },
    ];

    override get capacityInfo(): {
        [key in PaxType]?: {
            count: number;
            min: number;
            max: number;
        };
    } {
        return {
            [PaxType.Adult]: {
                count: this.adultCount,
                min: 1,
                max: this.maxSeatCount,
            },
        };
    }

    override get reserveInfo(): ReserveInfo {
        if (this.reserveType === ReserveType.Bus) {
            return {
                type: ReserveType.Bus,
                title: 'اتوبوس',
                nameE: 'اتوبوسی',
                icon: faBus,
            };
        } else if (this.reserveType === ReserveType.Taxi) {
            return {
                type: ReserveType.Taxi,
                title: 'سواری',
                nameE: 'تاکسی و سواری',
                icon: faCar,
            };
        } else {
            throw new Error('reserve type not found!');
        }
    }

    override get viewUrl() {
        return this.generateViewUrl({
            origin: this.originCode!,
            destination: this.destinationCode!,
            departureDate: this.formattedDepartureDate,
            returnDate:
                this.tripType === TripType.RoundTrip
                    ? this.formattedReturnDate
                    : undefined,
            adultCount: this.adultCount.toString(),
        });
    }

    override get popularForeignDestinations() {
        return this.foreignDestinations;
    }

    override async searchLocations(
        searchKeyword: string,
        options: { target: 'origin' | 'destination'; mode: SearchMode } = {
            target: 'origin',
            mode: SearchMode.Domestic,
        }
    ): Promise<void> {
        if (!this.originProvinces.length && options?.target === 'origin') {
            try {
                this.isLoadingLocations = true;
                if (this.reserveType === ReserveType.Bus) {
                    this.originProvinces = (
                        await import('~/lib/static/bus_stations.json')
                    ).default;
                } else {
                    this.originProvinces = (
                        await import('~/lib/static/taxi_stations.json')
                    ).default;
                }
            } finally {
                this.isLoadingLocations = false;
            }
        }
        await super.searchLocations(searchKeyword, options);
    }

    override getLocationKeywords(location: BusLocation) {
        return [
            ...super.getLocationKeywords(location),
            `${location.terminalId}`,
        ];
    }

    override get showRecentLocationsInDestinations() {
        return this.isDomestic;
    }

    async findMapRoute(busCode: string): Promise<string | null> {
        try {
            const { allRoutes } = await import('~/lib/static/contentBusMapped');
            return allRoutes.find(r => r.from === busCode)?.to ?? null;
        } catch (e) {
            reportException(e);
            return null;
        }
    }

    override setDestinationBaseOnCode(destinationCode: string) {
        if (!destinationCode) {
            return;
        }
        const location = [...this.destinations, ...this.origins].find(
            o => o.code === destinationCode
        );
        this.setDestination(location || null);
    }

    override async loadLandingSearchParams(destinationCode: string) {
        const origin = destinationCode === 'tehran' ? 'esfahan' : 'tehran';
        await this.searchLocations(origin);
        this.setOriginBaseOnCode(origin);
        await this.loadDestinations();
        await this.searchLocations(destinationCode, {
            target: 'destination',
            mode: SearchMode.Domestic,
        });
        this.setDestinationBaseOnCode(destinationCode);
    }
}
